const BODY_EXCERPT_LENGTH = 500;

/**
 * Sentry sends an error's name, message and stack, and drops any other
 * property unless `extraErrorDataIntegration` is registered (it is not), so
 * whatever must reach a report goes in the message.
 */
function excerpt(body: unknown): string {
  if (body === null || body === undefined || body === '') {
    return 'no body';
  }
  let text: string;
  try {
    text = typeof body === 'string' ? body : JSON.stringify(body);
  } catch {
    text = String(body);
  }
  return text.length > BODY_EXCERPT_LENGTH
    ? `${text.slice(0, BODY_EXCERPT_LENGTH)}…`
    : text;
}

/** The request that answered 401 and started a session check. */
export interface UnauthorizedRequest {
  readonly method: string;
  readonly url: string;
  readonly status: number;
  readonly responseBody: unknown;
}

function describe(request: UnauthorizedRequest): string {
  return `${request.method} ${request.url} answered ${request.status} (${excerpt(request.responseBody)})`;
}

/**
 * A request answered 401 while `/me` said the session was alive, and the
 * retry answered 401 again. The user is kept logged in; this carries what the
 * API sent so the endpoint that refused a valid session can be identified.
 */
export class SpuriousUnauthorizedError extends Error {
  readonly method: string;
  readonly url: string;
  readonly status: number;
  readonly responseBody: unknown;
  readonly meStatus: number;

  constructor(details: UnauthorizedRequest & { meStatus: number }) {
    super(`${describe(details)} while /me answered ${details.meStatus}`);
    this.name = 'SpuriousUnauthorizedError';
    this.method = details.method;
    this.url = details.url;
    this.status = details.status;
    this.responseBody = details.responseBody;
    this.meStatus = details.meStatus;
  }
}

/**
 * `/me` failed for a reason other than 401, so whether the session is alive
 * is unknown. `lastStatus` 0 means the request never reached the API.
 * `trigger` is the request whose 401 started the check, when one did.
 */
export class SessionCheckFailedError extends Error {
  readonly attempts: number;
  readonly lastStatus: number;
  readonly trigger: UnauthorizedRequest | undefined;

  constructor(
    attempts: number,
    lastStatus: number,
    trigger?: UnauthorizedRequest,
  ) {
    const cause = trigger ? ` after ${describe(trigger)}` : '';
    super(
      `/me failed ${attempts} time(s) without an answer about the session (last status ${lastStatus})${cause}`,
    );
    this.name = 'SessionCheckFailedError';
    this.attempts = attempts;
    this.lastStatus = lastStatus;
    this.trigger = trigger;
  }
}
